import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'
import store from '@/store'

import { query } from '@/_type/vbit-api'
import Verify from '@/views/Authentication/dialog/verify/verify.vue'
import DialogUserLog from '@/views/User/dialogs/userLog/userLog.vue'
import Log from '@/views/Log/models/log'
import CompanyAdminModel from '@/views/Authorization/models/companyAdmin'
import FrontEndSettingsModel from './frontEndSettings'

export default class UserModel extends JsonApiObject {
    static async search(query) {
        let res = await Vue.prototype.$vbit.get('user', 'users', query)

        let users = res.data.map((user) => new UserModel(user, res.included))

        return users
    }

    name
    jobTitle
    primaryEmail
    primaryEmailIsVerified
    mobileNumber
    mobileNumberIsVerified
    phoneNumber
    cardId
    language
    timeZone
    isSuperuser
    isEnabled
    updatedAt
    frontEndSettings: FrontEndSettingsModel

    public constructor(data: any, included?) {
        super(data, included)
        this.frontEndSettings = new FrontEndSettingsModel(data.attributes.frontEndSettings)
    }

    get initials() {
        if (typeof this.name !== 'string' || !this.name.trim()) return ''

        let initials = ''
        let words = this.name.split(/\s/)
        words.map((word, index) => {
            if (index == 0 || index == words.length - 1) {
                initials += word.slice(0, 1).toUpperCase()
            }
        })
        return initials
    }

    get isCurrentUser() {
        return store.getters.user ? this._id == store.getters.user.id : false
    }

    get _employer() {
        return this._relationships['employer'] ? this._relationships['employer'] : null
    }

    get hasEmployer() {
        return this._employer ? true : false
    }

    get employer() {
        return this.hasEmployer ? this._employer.name : null
    }

    get companyAdmin() {
        return this._relationships['companyAdmin'] ? this._relationships['companyAdmin'] : null
    }

    set companyAdmin(companyAdmin: CompanyAdminModel | null) {
        this._relationships['companyAdmin'] = companyAdmin
    }

    async showLog(user: UserModel) {
        await JsonApiObject.openDialog(DialogUserLog, { user: user })
    }

    async getLog(userId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': userId,
            'filter[time][lt]': timeStamp,
            'filter[activityLogNumber]': ['1', '11'],
        }

        let res = await Log.getLog(data)

        return res
    }

    static async getLog(userId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': userId,
            'filter[time][lt]': timeStamp,
            'filter[activityLogNumber]': ['1', '11'],
        }

        let res = await Log.getLog(data)

        return res
    }

    static async post(data: any, ReturnExistingEmailMobileErrors = false) {
        let query: query = {
            body: { data: data },
            skipLinks: true,
            ReturnExistingEmailMobileErrors,
        }
        let res = await Vue.prototype.$vbit.post('user', `users`, query).catch(async (error) => {
            if (error.code == 'proofRequired') {
                let confirm = await JsonApiObject.openDialog(Verify, {
                    claim: error.claim,
                    type: 'verify',
                })
                if (confirm) {
                    return await this.post(data)
                }
            } else {
                if (
                    error.detail.includes('mobileNumber') &&
                    error.title.includes('Invalid attribute value')
                ) {
                    store.commit('closeSnackbar')
                    error.code = 'invaliMobileNumber'
                }
                return error
            }
        })
        return res
    }

    static async patch(
        data: any,
        updatedAt: string,
        include: Array<'employer' | 'secondaryEmails'> = ['employer']
    ) {
        let query: query = {
            updatedAt: updatedAt,
            include: include,
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit
            .patch('user', `users/${data.id}`, query)
            .catch(async (error) => {
                if (error.code == 'proofRequired') {
                    let confirm = await JsonApiObject.openDialog(Verify, {
                        claim: error.claim,
                        type: 'verify',
                    })
                    if (confirm) {
                        return await this.patch(data, updatedAt, include)
                    } else {
                        return false
                    }
                } else {
                    return error
                }
            })

        if (res) {
            return res
        }
    }

    static async get(id: string, include: Array<'employer' | 'secondaryEmails'> = ['employer']) {
        let query: query = {
            include: include,
        }

        let res = await Vue.prototype.$vbit.get('user', `users/${id}`, query)
        return new UserModel(res.data, res.included)
    }

    static async getCurrentUser(include: Array<'employer' | 'secondaryEmails'> = ['employer']) {
        let query: query = {
            filters: {
                'filter[isCurrent]': true,
                'filter[kind]': 'normal',
            },
            include: include,
        }

        let res = await Vue.prototype.$vbit.get('user', 'users', query)

        store.commit('user', res.data.length > 0 ? res.data[0] : null)

        this.storeUser(res)

        return res.data.length > 0 ? res.data[0] : null
    }

    static async searchUsers(query) {
        let res = await Vue.prototype.$vbit.get('user', 'users', query)

        return res.data.map((user) => new UserModel(user, res.included))
    }

    static async confirmEmail(token: string) {
        let data = {
            type: 'secondaryEmail',
            attributes: {
                token: token,
            },
        }

        let query: query = {
            body: { data: data },
            skipLinks: true,
        }
        let res = await Vue.prototype.$vbit.post('user', `secondaryEmails`, query)

        return res.data
    }

    static async postSecondaryEmail(userId: string, email: string) {
        let query = {
            body: {
                data: {
                    type: 'secondaryEmail',
                    attributes: {
                        email,
                    },
                    relationships: {
                        user: {
                            data: {
                                type: 'user',
                                id: userId,
                            },
                        },
                    },
                },
            },
        }

        let res = await Vue.prototype.$vbit
            .post('user', `secondaryEmails`, query)
            .catch(async (error) => {
                if (error.code == 'proofRequired') {
                    let confirm = await JsonApiObject.openDialog(Verify, {
                        claim: error.claim,
                        type: 'verify',
                    })
                    if (confirm) {
                        let confirmRes = await this.postSecondaryEmail(userId, email)
                        return confirmRes
                    }
                } else if (error.code == 'emailExistsOnOtherUser') {
                    return 'emailExistsOnOtherUser'
                } else if (error.code == 'emailAlreadyInAccount') {
                    return 'emailAlreadyInAccount'
                }
            })

        if (store.getters.existOnOtherUserEmailVerification) {
            store.commit('existOnOtherUserEmailVerification', false)
        }
        return res
    }

    static storeUser(res) {
        let company =
            res.included.length > 0 ? res.included.find((item) => item._type == 'company') : null

        store.commit('userCompany', company)

        store.commit(
            'secondaryEmails',
            res.included.length > 0
                ? res.included
                      .filter((item) => item.type == 'secondaryEmail')
                      .reduce((a, v) => ({ ...a, [v.id]: v }), {})
                : null
        )
    }

    static async patchCurrentUser(
        data: any,
        updatedAt: string,
        include: Array<'employer' | 'secondaryEmails'> = ['employer']
    ) {
        let res = await this.patch(data, updatedAt, include)

        if (res && res.data) {
            store.commit('user', res.data)
            this.storeUser(res)
        }

        return res
    }

    static async verify(
        attr: 'mobileNumberIsVerified' | 'primaryEmailIsVerified',
        id: string,
        updatedAt: string,
        include: Array<'employer' | 'secondaryEmails'> = ['employer']
    ) {
        let data = {
            type: 'user',
            id: id,
            attributes: {
                [attr]: true,
            },
        }

        await UserModel.patchCurrentUser(data, updatedAt, include)
        return true
    }
}
